import {
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  startOfYear,
  endOfYear,
  subYears,
  differenceInCalendarDays,
} from "date-fns";
import { formatKhoangNgay } from "../LoiNhuan/periodUtils";

export const TUY_CHINH = "tuychinh";
export const KY_LIEN_TRUOC = "kylientruoc";

/** Tuần theo quy ước Việt Nam: bắt đầu từ Thứ 2 */
const OPT_TUAN = { weekStartsOn: 1 };

const ngay = (d) => ({ from: startOfDay(d), to: endOfDay(d) });
const tuan = (d) => ({
  from: startOfWeek(d, OPT_TUAN),
  to: endOfWeek(d, OPT_TUAN),
});
const thang = (d) => ({ from: startOfMonth(d), to: endOfMonth(d) });
const quy = (d) => ({ from: startOfQuarter(d), to: endOfQuarter(d) });
const nam = (d) => ({ from: startOfYear(d), to: endOfYear(d) });
const soNgayGanDay = (n) => ({
  from: startOfDay(subDays(new Date(), n - 1)),
  to: endOfDay(new Date()),
});

/**
 * Các mốc thời gian chọn nhanh.
 * `donVi` dùng để suy ra kỳ liền trước cho đúng đơn vị (tuần lùi 1 tuần,
 * tháng lùi 1 tháng... thay vì lùi theo số ngày).
 */
export const DANH_SACH_KY = [
  {
    value: "homnay",
    label: "Hôm nay",
    donVi: "ngay",
    tinh: () => ngay(new Date()),
  },
  {
    value: "homqua",
    label: "Hôm qua",
    donVi: "ngay",
    tinh: () => ngay(subDays(new Date(), 1)),
  },
  {
    value: "tuannay",
    label: "Tuần này",
    donVi: "tuan",
    tinh: () => tuan(new Date()),
  },
  {
    value: "tuantruoc",
    label: "Tuần trước",
    donVi: "tuan",
    tinh: () => tuan(subWeeks(new Date(), 1)),
  },
  {
    value: "thangnay",
    label: "Tháng này",
    donVi: "thang",
    tinh: () => thang(new Date()),
  },
  {
    value: "thangtruoc",
    label: "Tháng trước",
    donVi: "thang",
    tinh: () => thang(subMonths(new Date(), 1)),
  },
  {
    value: "quynay",
    label: "Quý này",
    donVi: "quy",
    tinh: () => quy(new Date()),
  },
  {
    value: "quytruoc",
    label: "Quý trước",
    donVi: "quy",
    tinh: () => quy(subQuarters(new Date(), 1)),
  },
  {
    value: "namnay",
    label: "Năm nay",
    donVi: "nam",
    tinh: () => nam(new Date()),
  },
  {
    value: "namtruoc",
    label: "Năm trước",
    donVi: "nam",
    tinh: () => nam(subYears(new Date(), 1)),
  },
  {
    value: TUY_CHINH,
    label: "Tuỳ chỉnh thời gian",
    donVi: "ngay",
    tinh: null,
  },
];

/** Options cho ô "Lọc theo thời gian" */
export const OPTIONS_KY = DANH_SACH_KY.map(({ value, label }) => ({
  value,
  label,
}));

/** Options cho ô "So sánh với" — thêm lựa chọn tự động lùi 1 kỳ */
export const OPTIONS_KY_SO_SANH = [
  { value: KY_LIEN_TRUOC, label: "Kỳ liền trước (tự động)" },
  ...OPTIONS_KY,
];

const timKy = (value) => DANH_SACH_KY.find((k) => k.value === value);

const dongGoi = (from, to, nhan) => ({
  from,
  to,
  nhan,
  khoangNgay: formatKhoangNgay(from, to),
});

/**
 * Quy đổi lựa chọn trên bộ lọc thành khoảng ngày cụ thể.
 * Trả về null khi chọn "Tuỳ chỉnh" mà chưa nhập đủ 2 mốc ngày.
 */
export const tinhKhoangThoiGian = (value, tuNgay, denNgay) => {
  if (value === TUY_CHINH) {
    if (!tuNgay || !denNgay) return null;
    const from = startOfDay(tuNgay);
    const to = endOfDay(denNgay);
    // Người dùng nhập ngược thứ tự thì tự đảo lại cho đúng
    return from > to
      ? dongGoi(startOfDay(denNgay), endOfDay(tuNgay), "Tuỳ chỉnh")
      : dongGoi(from, to, "Tuỳ chỉnh");
  }

  const ky = timKy(value);
  if (!ky || !ky.tinh) return null;
  const { from, to } = ky.tinh();
  return dongGoi(from, to, ky.label);
};

/**
 * Kỳ liền trước của một khoảng thời gian.
 * - Chọn nhanh theo tuần/tháng/quý/năm → lùi đúng 1 đơn vị tương ứng
 * - Còn lại (theo ngày hoặc tuỳ chỉnh) → lùi đúng bằng độ dài khoảng lọc
 */
export const layKyLienTruoc = (value, khoang) => {
  if (!khoang) return null;
  const donVi = (timKy(value) || {}).donVi || "ngay";

  switch (donVi) {
    case "tuan": {
      const goc = subWeeks(khoang.from, 1);
      const { from, to } = tuan(goc);
      return dongGoi(from, to, "Kỳ liền trước");
    }
    case "thang": {
      const goc = subMonths(khoang.from, 1);
      const { from, to } = thang(goc);
      return dongGoi(from, to, "Kỳ liền trước");
    }
    case "quy": {
      const goc = subQuarters(khoang.from, 1);
      const { from, to } = quy(goc);
      return dongGoi(from, to, "Kỳ liền trước");
    }
    case "nam": {
      const goc = subYears(khoang.from, 1);
      const { from, to } = nam(goc);
      return dongGoi(from, to, "Kỳ liền trước");
    }
    default: {
      const soNgay = differenceInCalendarDays(khoang.to, khoang.from) + 1;
      const to = endOfDay(subDays(khoang.from, 1));
      const from = startOfDay(subDays(to, soNgay - 1));
      return dongGoi(from, to, "Kỳ liền trước");
    }
  }
};

/**
 * Gộp bộ lọc thành 2 khoảng thời gian: kỳ đang xem và kỳ so sánh.
 * @returns {{ kyNay: object|null, kyTruoc: object|null }}
 */
export const layHaiKy = (boLoc) => {
  const kyNay = tinhKhoangThoiGian(boLoc.ky, boLoc.tuNgay, boLoc.denNgay);

  let kyTruoc = null;
  if (kyNay) {
    kyTruoc =
      boLoc.kySoSanh === KY_LIEN_TRUOC
        ? layKyLienTruoc(boLoc.ky, kyNay)
        : tinhKhoangThoiGian(boLoc.kySoSanh, boLoc.ssTuNgay, boLoc.ssDenNgay);
  }

  return { kyNay, kyTruoc };
};
